import type { ReactNode } from 'react';
import {
  ClipboardCheck,
  ClipboardList,
  GraduationCap,
  Package,
  Flag,
  Archive,
} from 'lucide-react';
import type { MobilisationEvent } from '@hcc/types';
import type { PillTone } from '../../../components/ui/Pill';
import { usePersonOnboarding } from '../../../hooks/usePersonOnboarding';
import {
  preflightSummary,
  outstandingTaskCount,
  kitReceiptSummary,
  activeFlagCount,
  isArchived,
} from './summaryMetrics';
import type { PersonTabId } from './tabs';
import type { ActiveCheck } from './types';

const TONE_CLASSES: Record<PillTone, string> = {
  success: 'bg-success-soft/15 text-success-soft-fg',
  warning: 'bg-warning-soft/15 text-warning-soft-fg',
  danger: 'bg-danger-soft/15 text-danger-soft-fg',
  info: 'bg-info-soft/15 text-info-soft-fg',
  accent: 'bg-accent/10 text-accent',
  neutral: 'bg-neutral-soft/10 text-neutral-soft-fg',
};

/**
 * At-a-glance status pills under the person header. Every metric is
 * derived from data the page already holds (metadata, checks, events)
 * plus the shared onboarding query; clicking a pill jumps to the tab
 * that owns the detail.
 */
export function SummaryStrip({
  personId,
  metadata,
  checks,
  events,
  visibleTabs,
  onNavigate,
}: {
  personId: string;
  metadata: Record<string, unknown>;
  checks: ActiveCheck[];
  events: MobilisationEvent[] | null;
  visibleTabs: PersonTabId[];
  onNavigate: (tab: PersonTabId) => void;
}) {
  const onboarding = usePersonOnboarding(personId);

  const preflight = preflightSummary(metadata, checks);
  const openTasks = outstandingTaskCount(events);
  const kit = kitReceiptSummary(events);
  const flags = activeFlagCount(events);
  const archived = isArchived(events);

  const preflightTone: PillTone = preflight.anyMissing
    ? 'danger'
    : preflight.anyPending
    ? 'warning'
    : 'success';

  return (
    <div className="flex flex-wrap gap-2">
      <SummaryPill
        tone={preflightTone}
        icon={<ClipboardCheck className="h-3.5 w-3.5" />}
        label={`Checks ${preflight.done}/${preflight.total}`}
        destination="Profile"
        onClick={() => onNavigate('profile')}
      />
      <SummaryPill
        tone={openTasks > 0 ? 'warning' : 'neutral'}
        icon={<ClipboardList className="h-3.5 w-3.5" />}
        label={openTasks === 1 ? '1 open task' : `${openTasks} open tasks`}
        destination="Onboarding"
        onClick={() => onNavigate('onboarding')}
      />
      {onboarding.data && onboarding.data.total > 0 ? (
        <SummaryPill
          tone={onboarding.data.done === onboarding.data.total ? 'success' : 'info'}
          icon={<GraduationCap className="h-3.5 w-3.5" />}
          label={`Onboarding ${onboarding.data.done}/${onboarding.data.total}`}
          destination="Onboarding"
          onClick={() => onNavigate('onboarding')}
        />
      ) : null}
      <SummaryPill
        tone={kit.done === kit.total ? 'success' : 'neutral'}
        icon={<Package className="h-3.5 w-3.5" />}
        label={`Kit ${kit.done}/${kit.total} stations`}
        destination="Kit & Certificates"
        onClick={() => onNavigate('kit')}
      />
      {flags > 0 ? (
        <SummaryPill
          tone="danger"
          icon={<Flag className="h-3.5 w-3.5" />}
          label={flags === 1 ? '1 active flag' : `${flags} active flags`}
          destination="History & Audit"
          onClick={() => onNavigate('history')}
        />
      ) : null}
      {archived ? (
        <SummaryPill
          tone="warning"
          icon={<Archive className="h-3.5 w-3.5" />}
          label="Archived"
          destination={visibleTabs.includes('admin') ? 'Admin' : 'History & Audit'}
          onClick={() =>
            onNavigate(visibleTabs.includes('admin') ? 'admin' : 'history')
          }
        />
      ) : null}
    </div>
  );
}

function SummaryPill({
  tone,
  icon,
  label,
  destination,
  onClick,
}: {
  tone: PillTone;
  icon: ReactNode;
  label: string;
  destination: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}. Go to ${destination}.`}
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-shadow',
        'hover:ring-2 hover:ring-line/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong',
        TONE_CLASSES[tone],
      ].join(' ')}
    >
      <span aria-hidden className="inline-flex shrink-0">
        {icon}
      </span>
      {label}
    </button>
  );
}
